import { useCallback } from 'react'
import { toast } from '../services/toastService'
import { getDefaultMapCenter } from '../utils/mapUtils'
import { storage } from '../utils/storage'
import { useLoading } from '../context/LoadingContext'
import { createLogger } from '../utils/logger'

const logger = createLogger('useMapLocation')

/**
 * Custom hook for map location management
 * Provides utilities for getting user location with persistence
 */
export function useMapLocation({ t, fetchCallback, reverseGeocode }) {
  const { startLoading, stopLoading } = useLoading()
  /**
   * Get the default location from env vars
   */
  const getDefaultLocation = useCallback(() => {
    const [lat, lng] = getDefaultMapCenter()
    const defaultCity = import.meta.env.VITE_DEFAULT_CITY || 'Denver, CO'
    return { name: defaultCity, lat, lng }
  }, [])

  /**
   * Get user's current location using browser geolocation API
   * Falls back to default location if geolocation fails or is denied
   * Asks user for permission before persisting location (privacy-friendly)
   * 
   * @param {Object} options - Configuration options
   * @param {Function} options.onSuccess - Callback with location data
   * @param {Function} options.onError - Callback on error
   * @param {React.MutableRefObject} options.hasShownErrorRef - Ref to track if error was shown
   */
  const getUserLocation = useCallback(({ onSuccess, onError, hasShownErrorRef }) => {
    const operationId = `geolocation-${Date.now()}`
    startLoading(operationId)
    
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }

          // Reverse geocode to get address
          let locationName = `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`
          try {
            const result = await reverseGeocode(location.latitude, location.longitude)
            if (result) {
              locationName = result.formattedAddress
            }
          } catch (error) {
            logger.error('Reverse geocoding failed', error)
          }
          
          // Show opt-in toast for saving location (privacy-friendly)
          // Check if user hasn't already been asked or has a saved location
          const existingLocation = storage.get('lastMapLocation', null)
          const hasBeenAsked = storage.get('locationSavePromptShown', false)
          
          if (!existingLocation && !hasBeenAsked) {
            // Show informational toast - location will be saved on next visit if user uses GPS again
            // This is a passive opt-in: using GPS implies consent to remember location
            storage.set('lastMapLocation', {
              latitude: location.latitude,
              longitude: location.longitude,
              name: locationName,
              timestamp: Date.now()
            })
            storage.set('locationSavePromptShown', true)
            
            // Inform user that location has been saved for convenience
            toast.info(t('map.locationSavedForFutureVisits', 'Your location has been saved for future visits. You can clear this in your browser settings.'), {
              autoClose: 5000,
              position: 'bottom-center'
            })
          }

          if (onSuccess) {
            onSuccess(location, locationName)
          }

          if (fetchCallback) {
            fetchCallback(location)
          }
          
          stopLoading(operationId)
        },
        (error) => {
          stopLoading(operationId)
          
          // Handle geolocation errors - use default location as fallback
          const defaultLoc = getDefaultLocation()
          
          if (hasShownErrorRef && !hasShownErrorRef.current) {
            const errorMessage = error.code === 1
              ? t('map.locationDenied')
              : t('map.locationUnavailable')
            toast.info(errorMessage)
            hasShownErrorRef.current = true
          }
          
          const locationObj = {
            latitude: defaultLoc.lat,
            longitude: defaultLoc.lng
          }

          if (onSuccess) {
            onSuccess(locationObj, defaultLoc.name)
          }

          if (fetchCallback) {
            fetchCallback(locationObj)
          }

          if (onError) {
            onError(defaultLoc)
          }
        },
        {
          timeout: 10000, // 10 second timeout (was 30s - too long)
          maximumAge: 60000, // Accept 1min old location
          enableHighAccuracy: false // Faster, less battery drain
        }
      )
    } else {
      stopLoading(operationId)
      
      // Geolocation not supported - use default location
      const defaultLoc = getDefaultLocation()
      
      if (hasShownErrorRef && !hasShownErrorRef.current) {
        toast.info(t('map.geolocationNotSupported'))
        hasShownErrorRef.current = true
      }
      
      const locationObj = {
        latitude: defaultLoc.lat,
        longitude: defaultLoc.lng
      }

      if (onSuccess) {
        onSuccess(locationObj, defaultLoc.name)
      }

      if (fetchCallback) {
        fetchCallback(locationObj)
      }

      if (onError) {
        onError(defaultLoc)
      }
    }
  }, [t, fetchCallback, reverseGeocode, getDefaultLocation, startLoading, stopLoading])

  /**
   * Get the last saved location from localStorage
   * @returns {Object|null} Last location or null if not found
   */
  const getLastSavedLocation = useCallback(() => {
    return storage.get('lastMapLocation', null)
  }, [])

  /**
   * Save a location to localStorage for persistence
   * @param {Object} location - Location object with latitude, longitude, name
   */
  const saveLocation = useCallback((location) => {
    storage.set('lastMapLocation', {
      ...location,
      timestamp: Date.now()
    })
  }, [])

  return {
    getUserLocation,
    getLastSavedLocation,
    saveLocation,
    getDefaultLocation
  }
}
